/**
 * useMentions Hook
 *
 * Provides @mention autocomplete functionality for chat input.
 *
 * Features:
 * - Detect @mention trigger in text input
 * - Search users and agents by name
 * - Keyboard navigation for suggestions
 * - Format mentions for display and API
 * - Extract mention IDs from message content
 *
 * @module hooks/useMentions
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

// ============================================
// Constants
// ============================================

/**
 * Debounce delay for search queries in milliseconds
 */
const SEARCH_DEBOUNCE_MS = 150;

/**
 * Maximum number of suggestions to show
 */
const MAX_SUGGESTIONS = 8;

/**
 * Minimum characters after @ to start searching
 */
const MIN_SEARCH_LENGTH = 0;

/**
 * Maximum mentions allowed per message
 */
const MAX_MENTIONS_PER_MESSAGE = 10;

/**
 * Mention types
 */
export const MENTION_TYPES = {
  USER: 'user',
  AGENT: 'agent'
};

/**
 * Regex pattern to match @mentions in text
 * Matches @ followed by lowercase letters, numbers, and hyphens
 */
const MENTION_PATTERN = /@([a-z][a-z0-9-]*)/gi;

/**
 * Regex pattern to detect active mention being typed
 * Matches @ at end of text or @ followed by valid mention chars
 */
const ACTIVE_MENTION_PATTERN = /@([a-z0-9-]*)$/i;

// ============================================
// Helper Functions
// ============================================

/**
 * Extract all mentions from message content
 *
 * @param {string} content - Message content
 * @returns {Array<{type: string, id: string, name: string}>} Array of mentions
 */
export function extractMentionsFromContent(content) {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const mentions = [];
  const seen = new Set();

  let match;
  // Reset regex lastIndex
  MENTION_PATTERN.lastIndex = 0;

  while ((match = MENTION_PATTERN.exec(content)) !== null) {
    const name = match[1].toLowerCase();

    // Deduplicate mentions
    if (!seen.has(name)) {
      seen.add(name);

      mentions.push({
        type: MENTION_TYPES.AGENT, // Default to agent, resolved later
        id: name,
        name
      });

      // Respect maximum mentions limit
      if (mentions.length >= MAX_MENTIONS_PER_MESSAGE) {
        break;
      }
    }
  }

  return mentions;
}

/**
 * Find the active mention being typed at cursor position
 *
 * @param {string} text - Input text
 * @param {number} cursorPosition - Cursor position in text
 * @returns {{query: string, startIndex: number, endIndex: number} | null} Active mention info or null
 */
export function findActiveMention(text, cursorPosition) {
  if (!text || typeof text !== 'string' || cursorPosition < 0) {
    return null;
  }

  // Get text before cursor
  const textBeforeCursor = text.slice(0, cursorPosition);

  // Find the last @ before cursor
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');

  if (lastAtIndex === -1) {
    return null;
  }

  // Check if @ is preceded by whitespace or is at the start
  if (lastAtIndex > 0) {
    const charBefore = textBeforeCursor[lastAtIndex - 1];
    if (!/\s/.test(charBefore)) {
      return null;
    }
  }

  // Get the text after @ up to cursor
  const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);

  // Check if the text after @ is a valid partial mention (no whitespace)
  if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
    return null;
  }

  // Validate the query characters
  if (textAfterAt && !/^[a-z0-9-]*$/i.test(textAfterAt)) {
    return null;
  }

  // Find where the mention ends (next whitespace or end of string)
  let endIndex = cursorPosition;
  while (endIndex < text.length && /[a-z0-9-]/i.test(text[endIndex])) {
    endIndex++;
  }

  return {
    query: textAfterAt.toLowerCase(),
    startIndex: lastAtIndex,
    endIndex
  };
}

/**
 * Insert mention at the active mention position
 *
 * @param {string} text - Current input text
 * @param {number} cursorPosition - Current cursor position
 * @param {object} mention - Mention to insert { name, type, id }
 * @returns {{text: string, cursorPosition: number} | null} New text and cursor position
 */
export function insertMention(text, cursorPosition, mention) {
  const activeMention = findActiveMention(text, cursorPosition);

  if (!activeMention || !mention) {
    return null;
  }

  const { startIndex, endIndex } = activeMention;
  const mentionText = `@${mention.name} `;

  const newText =
    text.slice(0, startIndex) +
    mentionText +
    text.slice(endIndex);

  const newCursorPosition = startIndex + mentionText.length;

  return {
    text: newText,
    cursorPosition: newCursorPosition
  };
}

/**
 * Format mention for display in text
 *
 * @param {string} name - Mention name
 * @returns {string} Formatted mention string
 */
export function formatMention(name) {
  return `@${name}`;
}

/**
 * Check if a character position is inside a mention
 *
 * @param {string} text - Text content
 * @param {number} position - Character position
 * @returns {boolean} True if position is inside a mention
 */
export function isInsideMention(text, position) {
  if (!text || position < 0 || position > text.length) {
    return false;
  }

  // Find all mentions in the text
  MENTION_PATTERN.lastIndex = 0;
  let match;

  while ((match = MENTION_PATTERN.exec(text)) !== null) {
    const start = match.index;
    const end = match.index + match[0].length;

    if (position >= start && position <= end) {
      return true;
    }
  }

  return false;
}

// ============================================
// Hook
// ============================================

/**
 * useMentions Hook
 *
 * Provides @mention autocomplete functionality for chat input.
 *
 * @param {object} options - Hook options
 * @param {string} [options.inputValue=''] - Current input value
 * @param {number} [options.cursorPosition=0] - Current cursor position
 * @param {Array<object>} [options.users=[]] - Available users for mentions
 * @param {Array<object>} [options.agents=[]] - Available agents for mentions
 * @param {Function} [options.onSearchUsers] - Optional async function to search users
 * @param {Function} [options.onSearchAgents] - Optional async function to search agents
 * @param {Function} [options.onMentionSelected] - Callback when a mention is selected
 *
 * @returns {object} Hook return value
 */
export function useMentions(options = {}) {
  const {
    inputValue = '',
    cursorPosition = 0,
    users = [],
    agents = [],
    onSearchUsers,
    onSearchAgents,
    onMentionSelected
  } = options;

  // ----------------------------------------
  // State
  // ----------------------------------------

  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeMentionInfo, setActiveMentionInfo] = useState(null);

  // ----------------------------------------
  // Refs
  // ----------------------------------------

  const searchTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  const searchCacheRef = useRef(new Map());

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  /**
   * Combined mentionable entities from users and agents
   */
  const mentionables = useMemo(() => {
    const combined = [];

    // Add users
    users.forEach((user) => {
      combined.push({
        id: user.id,
        name: user.name || user.username || user.id,
        displayName: user.displayName || user.name || user.username,
        type: MENTION_TYPES.USER,
        avatarUrl: user.avatarUrl || null,
        isOnline: user.isOnline || false
      });
    });

    // Add agents
    agents.forEach((agent) => {
      combined.push({
        id: agent.id,
        name: agent.name || agent.id,
        displayName: agent.displayName || agent.name,
        type: MENTION_TYPES.AGENT,
        avatarUrl: agent.avatarUrl || null,
        isOnline: agent.operationalStatus !== 'offline',
        operationalStatus: agent.operationalStatus || 'offline'
      });
    });

    return combined;
  }, [users, agents]);

  // ----------------------------------------
  // Search Functions
  // ----------------------------------------

  /**
   * Filter local mentionables by query
   */
  const filterLocalMentionables = useCallback(
    (query) => {
      if (!query) {
        return mentionables.slice(0, MAX_SUGGESTIONS);
      }

      const normalizedQuery = query.toLowerCase();

      return mentionables
        .filter((m) => {
          const name = (m.name || '').toLowerCase();
          const displayName = (m.displayName || '').toLowerCase();
          return (
            name.includes(normalizedQuery) ||
            displayName.includes(normalizedQuery)
          );
        })
        .sort((a, b) => {
          // Prioritize exact prefix matches
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          const aStartsWith = aName.startsWith(normalizedQuery);
          const bStartsWith = bName.startsWith(normalizedQuery);

          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;

          // Then by name length (shorter first)
          return aName.length - bName.length;
        })
        .slice(0, MAX_SUGGESTIONS);
    },
    [mentionables]
  );

  /**
   * Search for mentionables (combines local filter with remote search)
   */
  const searchMentionables = useCallback(
    async (query) => {
      // Check cache first
      const cacheKey = query.toLowerCase();
      if (searchCacheRef.current.has(cacheKey)) {
        return searchCacheRef.current.get(cacheKey);
      }

      // Start with local filtering
      let results = filterLocalMentionables(query);

      // If we have remote search functions, use them
      if (onSearchUsers || onSearchAgents) {
        setIsLoading(true);
        setError(null);

        try {
          const searchPromises = [];

          if (onSearchUsers) {
            searchPromises.push(
              onSearchUsers(query).then((users) =>
                users.map((u) => ({
                  ...u,
                  type: MENTION_TYPES.USER
                }))
              )
            );
          }

          if (onSearchAgents) {
            searchPromises.push(
              onSearchAgents(query).then((agents) =>
                agents.map((a) => ({
                  ...a,
                  type: MENTION_TYPES.AGENT
                }))
              )
            );
          }

          const remoteResults = await Promise.all(searchPromises);
          const combined = remoteResults.flat();

          // Merge with local results, avoiding duplicates
          const seenIds = new Set(results.map((r) => r.id));
          combined.forEach((item) => {
            if (!seenIds.has(item.id)) {
              seenIds.add(item.id);
              results.push(item);
            }
          });

          // Sort and limit
          results = results.slice(0, MAX_SUGGESTIONS);
        } catch (err) {
          setError(err.message || 'Failed to search');
          // Fall back to local results on error
        } finally {
          setIsLoading(false);
        }
      }

      // Cache the results
      searchCacheRef.current.set(cacheKey, results);

      // Limit cache size
      if (searchCacheRef.current.size > 100) {
        const firstKey = searchCacheRef.current.keys().next().value;
        searchCacheRef.current.delete(firstKey);
      }

      return results;
    },
    [filterLocalMentionables, onSearchUsers, onSearchAgents]
  );

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  /**
   * Detect active mention and update suggestions
   */
  // Track values in refs to avoid dependency cycles
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const searchMentionablesRef = useRef(searchMentionables);
  searchMentionablesRef.current = searchMentionables;

  useEffect(() => {
    const activeMention = findActiveMention(inputValue, cursorPosition);

    if (activeMention) {
      const { query } = activeMention;

      // Skip if query hasn't changed and dropdown is already open
      if (query === lastSearchQueryRef.current && isOpenRef.current) {
        return;
      }

      lastSearchQueryRef.current = query;
      setActiveMentionInfo(activeMention);

      // Debounce search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        const results = await searchMentionablesRef.current(query);
        setSuggestions(results);
        setSelectedIndex(0);
        setIsOpen(results.length > 0);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      // No active mention, close dropdown
      setIsOpen(false);
      setSuggestions([]);
      setSelectedIndex(0);
      setActiveMentionInfo(null);
      lastSearchQueryRef.current = '';
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputValue, cursorPosition]);

  // ----------------------------------------
  // Actions
  // ----------------------------------------

  /**
   * Select a mention from suggestions
   */
  const selectMention = useCallback(
    (mention) => {
      if (!mention || !activeMentionInfo) {
        return null;
      }

      const result = insertMention(inputValue, cursorPosition, mention);

      if (result && onMentionSelected) {
        onMentionSelected(mention, result);
      }

      // Close dropdown
      setIsOpen(false);
      setSuggestions([]);
      setSelectedIndex(0);
      setActiveMentionInfo(null);

      return result;
    },
    [inputValue, cursorPosition, activeMentionInfo, onMentionSelected]
  );

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen || suggestions.length === 0) {
        return false;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          return true;

        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          return true;

        case 'Enter':
        case 'Tab':
          if (suggestions[selectedIndex]) {
            event.preventDefault();
            selectMention(suggestions[selectedIndex]);
            return true;
          }
          break;

        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          return true;

        default:
          break;
      }

      return false;
    },
    [isOpen, suggestions, selectedIndex, selectMention]
  );

  /**
   * Close the dropdown
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setSuggestions([]);
    setSelectedIndex(0);
    setActiveMentionInfo(null);
  }, []);

  /**
   * Clear the search cache
   */
  const clearCache = useCallback(() => {
    searchCacheRef.current.clear();
  }, []);

  /**
   * Get mentions from text (wrapper for extract function)
   */
  const getMentions = useCallback((text) => {
    return extractMentionsFromContent(text);
  }, []);

  /**
   * Resolve mention IDs from names using available users and agents
   */
  const resolveMentionIds = useCallback(
    (mentions) => {
      const userIds = [];
      const agentIds = [];

      mentions.forEach((mention) => {
        const name = mention.name || mention.id;

        // Try to find in users
        const matchedUser = users.find(
          (u) =>
            (u.name || u.username || u.id).toLowerCase() === name.toLowerCase()
        );
        if (matchedUser) {
          userIds.push(matchedUser.id);
          return;
        }

        // Try to find in agents - check name, displayName, and id
        const matchedAgent = agents.find(
          (a) => {
            const agentName = (a.name || '').toLowerCase();
            const agentDisplayName = (a.displayName || '').toLowerCase();
            const agentId = (a.id || '').toLowerCase();
            const searchName = name.toLowerCase();
            return agentName === searchName || agentDisplayName === searchName || agentId === searchName;
          }
        );
        if (matchedAgent) {
          agentIds.push(matchedAgent.id);
        }
      });

      return { userIds, agentIds };
    },
    [users, agents]
  );

  // ----------------------------------------
  // Return Value
  // ----------------------------------------

  return {
    // State
    isOpen,
    suggestions,
    selectedIndex,
    isLoading,
    error,
    activeMentionInfo,

    // Actions
    selectMention,
    handleKeyDown,
    close,
    clearCache,
    getMentions,
    resolveMentionIds,

    // Setters (for external control)
    setSelectedIndex,

    // Utilities
    extractMentionsFromContent,
    findActiveMention,
    insertMention,
    formatMention,
    isInsideMention,

    // Constants
    MENTION_TYPES,
    MAX_SUGGESTIONS,
    MAX_MENTIONS_PER_MESSAGE
  };
}

// ============================================
// Convenience Hook
// ============================================

/**
 * useSimpleMentions Hook
 *
 * A simplified version that only provides mention extraction
 * without autocomplete functionality.
 *
 * @returns {object} Hook return value
 */
export function useSimpleMentions() {
  /**
   * Extract mentions from content
   */
  const getMentions = useCallback((content) => {
    return extractMentionsFromContent(content);
  }, []);

  /**
   * Format a mention for display
   */
  const format = useCallback((name) => {
    return formatMention(name);
  }, []);

  /**
   * Check if text contains mentions
   */
  const hasMentions = useCallback((content) => {
    if (!content || typeof content !== 'string') {
      return false;
    }
    MENTION_PATTERN.lastIndex = 0;
    return MENTION_PATTERN.test(content);
  }, []);

  /**
   * Count mentions in text
   */
  const countMentions = useCallback((content) => {
    return extractMentionsFromContent(content).length;
  }, []);

  /**
   * Highlight mentions in text (returns segments)
   */
  const highlightMentions = useCallback((content) => {
    if (!content || typeof content !== 'string') {
      return [{ text: content || '', isMention: false }];
    }

    const segments = [];
    let lastIndex = 0;

    MENTION_PATTERN.lastIndex = 0;
    let match;

    while ((match = MENTION_PATTERN.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        segments.push({
          text: content.slice(lastIndex, match.index),
          isMention: false
        });
      }

      // Add mention
      segments.push({
        text: match[0],
        isMention: true,
        name: match[1].toLowerCase()
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      segments.push({
        text: content.slice(lastIndex),
        isMention: false
      });
    }

    return segments;
  }, []);

  return {
    getMentions,
    format,
    hasMentions,
    countMentions,
    highlightMentions,
    MENTION_TYPES,
    MAX_MENTIONS_PER_MESSAGE
  };
}

// ============================================
// Default Export
// ============================================

export default useMentions;
